import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { BottomNavigation } from '../components/BottomNavigation';
import { 
  ArrowLeft,
  Star,
  Heart,
  Calendar,
  TimeCircle,
  Location,
  Buy,
  User
} from 'react-iconly';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { BackButton } from '../components/ui';
import { trackProductView, generateSessionId } from '../lib/analyticsService';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

// Allergen icons mapping
const allergenIcons = {
  'Egg': '🥚',
  'Nuts': '🥜',
  'Shellfish': '🦐',
  'Dairy': '🥛',
  'Gluten': '🌾',
  'Soy': '🫘',
  'Fish': '🐟',
  'Wheat': '🌾'
};

// Dummy food detail data
const dummyFoodDetail = {
  id: 1,
  name: 'Handmade Pasta Carbonara',
  vendor: 'Chef Mario\'s Kitchen',
  price: 18.99,
  rating: 4.8,
  reviews: 124,
  image: 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=800&h=600&fit=crop',
  category: 'Ready Now',
  cuisine: 'Italian',
  dietary: ['Vegetarian'],
  allergens: ['Egg', 'Dairy', 'Gluten'],
  description: 'Authentic Italian carbonara made with fresh handmade pasta, creamy egg sauce, crispy pancetta, and aged Pecorino Romano cheese. This classic Roman dish is prepared using traditional techniques passed down through generations. The pasta is cooked al dente and tossed with a rich, velvety sauce that coats every strand perfectly. Served with a generous sprinkling of black pepper and extra cheese on top.',
  ingredients: [
    'Fresh handmade fettuccine pasta',
    'Large eggs',
    'Pecorino Romano cheese',
    'Pancetta (Italian bacon)',
    'Black pepper',
    'Sea salt',
    'Extra virgin olive oil',
    'Fresh parsley (garnish)'
  ],
  variations: [
    { id: 1, name: 'Small', price: 14.99, description: 'Perfect for one person' },
    { id: 2, name: 'Medium', price: 18.99, description: 'Great for sharing', default: true },
    { id: 3, name: 'Large', price: 22.99, description: 'Family size portion' }
  ],
  spiceLevels: [
    { id: 1, name: 'Mild', description: 'Traditional Italian seasoning' },
    { id: 2, name: 'Medium', description: 'Extra black pepper', default: true },
    { id: 3, name: 'Spicy', description: 'Added red pepper flakes' }
  ],
  availability: [
    { date: '2024-01-15', day: 'Monday', available: true, slots: ['12:00 PM', '6:00 PM'] },
    { date: '2024-01-16', day: 'Tuesday', available: true, slots: ['12:00 PM', '6:00 PM'] },
    { date: '2024-01-17', day: 'Wednesday', available: true, slots: ['12:00 PM', '6:00 PM'] },
    { date: '2024-01-18', day: 'Thursday', available: false, slots: [] },
    { date: '2024-01-19', day: 'Friday', available: true, slots: ['12:00 PM', '6:00 PM'] },
    { date: '2024-01-20', day: 'Saturday', available: true, slots: ['11:00 AM', '5:00 PM'] },
    { date: '2024-01-21', day: 'Sunday', available: true, slots: ['11:00 AM', '5:00 PM'] }
  ],
  deliveryInfo: {
    deliveryTime: '30-45 minutes',
    serviceFee: 2.0,
    minOrder: 15.00,
    pickupAvailable: true
  },
  distance: 0.8
};

export function FoodDetail() {
  const { productId, foodId } = useParams();
  const navigate = useNavigate();
  
  // Handle both parameter names for backward compatibility
  const actualProductId = productId || foodId;
  
  console.log('[FoodDetail] productId from params:', productId);
  console.log('[FoodDetail] foodId from params:', foodId);
  console.log('[FoodDetail] actualProductId:', actualProductId);
  const [food, setFood] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shopName, setShopName] = useState('');
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedSpiceLevel, setSelectedSpiceLevel] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showFullIngredients, setShowFullIngredients] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const { cartItems, addCartItem, updateCartItem, removeCartItem } = useCart();
  const { currentUser } = useAuth();

  useEffect(() => {
    async function fetchProduct() {
      console.log('[FoodDetail] Fetching product with actualProductId:', actualProductId);
      setLoading(true);
      setError('');
      try {
        const productRef = doc(db, 'products', actualProductId);
        console.log('[FoodDetail] Product ref:', productRef);
        const productSnap = await getDoc(productRef);
        console.log('[FoodDetail] Product snapshot exists:', productSnap.exists());
        if (productSnap.exists()) {
          const data = { id: productSnap.id, ...productSnap.data() };
          setFood(data);
          setSelectedVariation(data.variations ? (data.variations.find(v => v.default) || data.variations[0]) : null);
          setSelectedSpiceLevel(data.spiceLevels ? (data.spiceLevels.find(s => s.default) || data.spiceLevels[0]) : null);
          
          // Track product view for analytics
          if (data.sellerId) {
            const sessionId = generateSessionId();
            trackProductView(data.id, data.sellerId, sessionId, currentUser?.uid);
          }
          // Fetch shop name by sellerId
          if (data.sellerId) {
            const shopsRef = collection(db, 'shops');
            const q = query(shopsRef, where('sellerId', '==', data.sellerId));
            const shopSnap = await getDocs(q);
            if (!shopSnap.empty) {
              setShopName(shopSnap.docs[0].data().name || '');
            } else {
              setShopName('');
            }
          } else {
            setShopName('');
          }
        } else {
          setError('Product not found.');
          setFood(null);
          setShopName('');
        }
      } catch (err) {
        setError('Failed to load product.');
        setFood(null);
        setShopName('');
      } finally {
        setLoading(false);
      }
    }
    if (actualProductId) fetchProduct();
  }, [actualProductId]);

  // Calculate total price based on selected variation
  const totalPrice = selectedVariation ? selectedVariation.price * quantity : (food?.price || 0) * quantity;

  // Helper to get current cart quantity for this food (by id only for now)
  const getCartQty = (foodId) => {
    const found = cartItems.find(i => i.id === foodId);
    return found ? found.qty : 0;
  };

  const handleAddToCart = () => {
    if (!selectedDate || !selectedTime) {
      alert('Please select a date and time');
      return;
    }

    const cartItem = {
      id: food.id,
      name: food.name,
      vendor: food.vendor,
      variation: selectedVariation?.name,
      spiceLevel: selectedSpiceLevel?.name,
      date: selectedDate,
      time: selectedTime,
      quantity: quantity,
      price: totalPrice,
      image: food.image
    };

    // Here you would typically add to cart state or send to API
    console.log('Adding to cart:', cartItem);
    alert('Added to cart!');
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(''); // Reset time when date changes
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const getAvailableSlots = (date) => {
    const dayData = food.availability.find(d => d.date === date);
    return dayData ? dayData.slots : [];
  };

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG, paddingBottom: 'calc(120px + env(safe-area-inset-bottom) + 56px)' }}>
      {loading ? (
        <div style={{ textAlign: 'center', marginTop: 80, color: ACCENT_GRAY }}>Loading product...</div>
      ) : error ? (
        <div style={{ textAlign: 'center', marginTop: 80, color: ERROR_RED }}>{error}</div>
      ) : !food ? null : (
        <>
          {/* Header with Back Button */}
          <header style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 16
          }}>
            <BackButton
              onClick={() => navigate(-1)}
              variant="standard"
              ariaLabel="Back"
            />
            <h1 style={{
              fontSize: 20,
              fontWeight: 700,
              color: NEUTRAL_TEXT,
              margin: 0,
              ...headingFont
            }}>
              {food.name}
            </h1>
          </header>

          {/* Hero Image */}
          <div style={{ marginTop: 72 }}>
            <img
              src={food.imageUrl || '/default-product.png'}
              alt={food.name}
              style={{
                width: '100%',
                height: 300,
                objectFit: 'cover'
              }}
              onError={e => { e.target.onerror = null; e.target.src = '/default-product.png'; }}
            />
          </div>

          {/* Food Information */}
          <div style={{ padding: '20px' }}>
            {/* Title and Rating */}
            <div style={{ marginBottom: 16 }}>
              <h1 style={{ 
                ...headingFont, 
                fontSize: 28, 
                color: NEUTRAL_TEXT, 
                marginBottom: 8,
                lineHeight: 1.2
              }}>
                {food.name}
              </h1>
              {shopName && food.sellerId && (
                <a
                  href={`/storefront/${food.sellerId}`}
                  style={{
                    color: PRIMARY_BROWN,
                    fontSize: 16,
                    fontWeight: 600,
                    marginBottom: 12,
                    textDecoration: 'none',
                    ...bodyFont
                  }}
                >
                  {shopName}
                </a>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Star set="bold" size={18} primaryColor="#FFD700" />
                <span style={{ fontSize: 16, color: NEUTRAL_TEXT, fontWeight: 600 }}>
                  {food.rating}
                </span>
                {Array.isArray(food.reviews) && food.reviews.length > 0 ? (
                  <button
                    onClick={() => navigate(`/food/${food.id}/reviews`)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: PRIMARY_BROWN,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      ...bodyFont
                    }}
                  >
                    ({food.reviews.length} reviews)
                  </button>
                ) : (
                  <span style={{ color: ACCENT_GRAY, fontSize: 15, ...bodyFont }}>Not yet reviewed</span>
                )}
                <span style={{ fontSize: 14, color: ACCENT_GRAY }}>
                  • {food.distance} mi away
                </span>
              </div>
            </div>

            {/* Dietary and Allergen Tags */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {Array.isArray(food.dietary) && food.dietary.map(diet => (
                  <span key={diet} style={{
                    background: PRIMARY_BROWN + '20',
                    color: PRIMARY_BROWN,
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: 12,
                    ...bodyFont
                  }}>
                    {diet}
                  </span>
                ))}
              </div>
              
              {/* Allergen Icons */}
              {Array.isArray(food.allergens) && food.allergens.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ 
                    ...headingFont, 
                    fontSize: 16, 
                    color: NEUTRAL_TEXT, 
                    marginBottom: 8 
                  }}>
                    Contains:
                  </h3>
                  <div style={{ display: 'flex', gap: 12 }}>
                    {Array.isArray(food.allergens) && food.allergens.map(allergen => (
                      <div key={allergen} style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        gap: 4
                      }}>
                        <span style={{ fontSize: 24 }}>{allergenIcons[allergen]}</span>
                        <span style={{ 
                          fontSize: 11, 
                          color: ACCENT_GRAY, 
                          fontWeight: 600,
                          ...bodyFont
                        }}>
                          {allergen}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                ...headingFont, 
                fontSize: 18, 
                color: NEUTRAL_TEXT, 
                marginBottom: 12 
              }}>
                Description
              </h3>
              <p style={{ 
                fontSize: 15, 
                color: NEUTRAL_TEXT, 
                lineHeight: 1.6,
                ...bodyFont
              }}>
                {showFullDescription ? food.description : food.description.slice(0, 150)}
                {food.description.length > 150 && (
                  <span>
                    {showFullDescription ? '' : '... '}
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: PRIMARY_BROWN,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: 15,
                        ...bodyFont
                      }}
                    >
                      {showFullDescription ? 'Read Less' : 'Read More'}
                    </button>
                  </span>
                )}
              </p>
            </div>

            {/* Ingredients */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                ...headingFont, 
                fontSize: 18, 
                color: NEUTRAL_TEXT, 
                marginBottom: 12 
              }}>
                Ingredients
              </h3>
              <div style={{ 
                background: NEUTRAL_WHITE, 
                borderRadius: 12, 
                padding: 16,
                border: '1px solid #E0E0E0'
              }}>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 8
                }}>
                  {Array.isArray(food.ingredients) && food.ingredients.slice(0, showFullIngredients ? food.ingredients.length : 6).map((ingredient, index) => (
                    <li key={index} style={{ 
                      fontSize: 14, 
                      color: NEUTRAL_TEXT,
                      padding: '4px 0',
                      ...bodyFont
                    }}>
                      • {ingredient}
                    </li>
                  ))}
                </ul>
                {Array.isArray(food.ingredients) && food.ingredients.length > 6 && (
                  <button
                    onClick={() => setShowFullIngredients(!showFullIngredients)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: PRIMARY_BROWN,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: 14,
                      marginTop: 12,
                      ...bodyFont
                    }}
                  >
                    {showFullIngredients ? 'Show Less' : `Show ${food.ingredients.length - 6} More`}
                  </button>
                )}
              </div>
            </div>

            {/* Variations */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                ...headingFont, 
                fontSize: 18, 
                color: NEUTRAL_TEXT, 
                marginBottom: 12 
              }}>
                Size
              </h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {Array.isArray(food.variations) && food.variations.map(variation => (
                  <button
                    key={variation.id}
                    onClick={() => setSelectedVariation(variation)}
                    style={{
                      background: selectedVariation?.id === variation.id ? PRIMARY_BROWN : NEUTRAL_WHITE,
                      color: selectedVariation?.id === variation.id ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                      border: `2px solid ${selectedVariation?.id === variation.id ? PRIMARY_BROWN : '#E0E0E0'}`,
                      borderRadius: 12,
                      padding: '12px 16px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      minHeight: 44,
                      flex: 1,
                      textAlign: 'left',
                      ...bodyFont
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{variation.name}</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{variation.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Spice Level */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                ...headingFont, 
                fontSize: 18, 
                color: NEUTRAL_TEXT, 
                marginBottom: 12 
              }}>
                Spice Level
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {Array.isArray(food.spiceLevels) && food.spiceLevels.map(spice => (
                  <button
                    key={spice.id}
                    onClick={() => setSelectedSpiceLevel(spice)}
                    style={{
                      background: selectedSpiceLevel?.id === spice.id ? PRIMARY_BROWN : NEUTRAL_WHITE,
                      color: selectedSpiceLevel?.id === spice.id ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                      border: `2px solid ${selectedSpiceLevel?.id === spice.id ? PRIMARY_BROWN : '#E0E0E0'}`,
                      borderRadius: 12,
                      padding: '12px 16px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      minHeight: 44,
                      flex: 1,
                      textAlign: 'center',
                      ...bodyFont
                    }}
                  >
                    {spice.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Availability */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                ...headingFont, 
                fontSize: 18, 
                color: NEUTRAL_TEXT, 
                marginBottom: 12 
              }}>
                Select Date & Time
              </h3>
              
              {/* Date Selection */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
                  {Array.isArray(food.availability) && food.availability.map(day => (
                    <button
                      key={day.date}
                      onClick={() => handleDateSelect(day.date)}
                      disabled={!day.available}
                      style={{
                        background: selectedDate === day.date ? PRIMARY_BROWN : NEUTRAL_WHITE,
                        color: selectedDate === day.date ? NEUTRAL_WHITE : (day.available ? NEUTRAL_TEXT : ACCENT_GRAY),
                        border: `2px solid ${selectedDate === day.date ? PRIMARY_BROWN : '#E0E0E0'}`,
                        borderRadius: 12,
                        padding: '12px 16px',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: day.available ? 'pointer' : 'not-allowed',
                        minHeight: 44,
                        minWidth: 80,
                        textAlign: 'center',
                        opacity: day.available ? 1 : 0.5,
                        ...bodyFont
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{day.day.slice(0, 3)}</div>
                      <div style={{ fontSize: 11, opacity: 0.8 }}>
                        {new Date(day.date).getDate()}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              {selectedDate && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {getAvailableSlots(selectedDate).map(time => (
                      <button
                        key={time}
                        onClick={() => handleTimeSelect(time)}
                        style={{
                          background: selectedTime === time ? PRIMARY_BROWN : NEUTRAL_WHITE,
                          color: selectedTime === time ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                          border: `2px solid ${selectedTime === time ? PRIMARY_BROWN : '#E0E0E0'}`,
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          minHeight: 40,
                          ...bodyFont
                        }}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quantity */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                ...headingFont, 
                fontSize: 18, 
                color: NEUTRAL_TEXT, 
                marginBottom: 12 
              }}>
                Quantity
              </h3>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 16,
                background: NEUTRAL_WHITE,
                borderRadius: 12,
                padding: 16,
                border: '1px solid #E0E0E0'
              }}>
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  style={{
                    background: NEUTRAL_BG,
                    border: 'none',
                    borderRadius: 8,
                    width: 40,
                    height: 40,
                    fontSize: 18,
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: NEUTRAL_TEXT
                  }}
                >
                  -
                </button>
                <span style={{ 
                  fontSize: 18, 
                  fontWeight: 600, 
                  color: NEUTRAL_TEXT,
                  minWidth: 40,
                  textAlign: 'center',
                  ...bodyFont
                }}>
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  style={{
                    background: NEUTRAL_BG,
                    border: 'none',
                    borderRadius: 8,
                    width: 40,
                    height: 40,
                    fontSize: 18,
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: NEUTRAL_TEXT
                  }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Delivery Info */}
            {food.deliveryInfo && (
              <div style={{ 
                background: NEUTRAL_WHITE, 
                borderRadius: 12, 
                padding: 16, 
                marginBottom: 24,
                border: '1px solid #E0E0E0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <TimeCircle set="regular" size={16} primaryColor={PRIMARY_BROWN} />
                  <span style={{ fontSize: 14, color: NEUTRAL_TEXT, fontWeight: 600, ...bodyFont }}>
                    Delivery: {food.deliveryInfo?.deliveryTime || 'N/A'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Location set="regular" size={16} primaryColor={PRIMARY_BROWN} />
                  <span style={{ fontSize: 14, color: NEUTRAL_TEXT, ...bodyFont }}>
                    Service fee: ${food.deliveryInfo?.serviceFee || 0}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                  Minimum order: ${food.deliveryInfo?.minOrder || 0}
                </div>
              </div>
            )}

          </div>

          {/* Fixed Bottom Bar */}
          <div style={{ 
            position: 'fixed', 
            bottom: 80, 
            left: 0, 
            right: 0, 
            background: NEUTRAL_WHITE, 
            borderTop: '1px solid #E0E0E0',
            padding: '16px 20px',
            zIndex: 100
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <div>
                <div style={{ 
                  ...headingFont, 
                  fontSize: 24, 
                  color: NEUTRAL_TEXT 
                }}>
                  ${totalPrice.toFixed(2)}
                </div>
                <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                  {selectedVariation?.name} • {selectedSpiceLevel?.name}
                </div>
              </div>
              {/* Add to Cart / Quantity Selector */}
              {getCartQty(food.id) === 0 ? (
                <button
                  onClick={() => {
                    if (!selectedDate || !selectedTime) return;
                    addCartItem({
                      id: food.id,
                      name: food.name,
                      image: food.image,
                      price: selectedVariation?.price,
                      qty: 1,
                      attributes: `${selectedVariation?.name}, ${selectedSpiceLevel?.name}, ${selectedDate}, ${selectedTime}`
                    });
                  }}
                  disabled={!selectedDate || !selectedTime}
                  style={{
                    background: (!selectedDate || !selectedTime) ? ACCENT_GRAY : PRIMARY_BROWN,
                    color: NEUTRAL_WHITE,
                    border: 'none',
                    borderRadius: 12,
                    padding: '14px 24px',
                    fontSize: 24,
                    fontWeight: 600,
                    cursor: (!selectedDate || !selectedTime) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 48,
                    minWidth: 48,
                    ...bodyFont
                  }}
                  aria-label="Add to cart"
                >
                  +
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: PRIMARY_BROWN, borderRadius: 12, padding: '4px 12px', minHeight: 48 }}>
                  {/* Decrement or Trash */}
                  {getCartQty(food.id) > 1 ? (
                    <button
                      onClick={() => updateCartItem(food.id, getCartQty(food.id) - 1)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: NEUTRAL_WHITE,
                        fontSize: 24,
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 8
                      }}
                      aria-label="Decrease quantity"
                    >-</button>
                  ) : (
                    <button
                      onClick={() => removeCartItem(food.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: NEUTRAL_WHITE,
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 8
                      }}
                      aria-label="Remove from cart"
                    >
                      {/* Trash can icon, white */}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" />
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  )}
                  {/* Quantity */}
                  <span style={{ color: NEUTRAL_WHITE, fontWeight: 600, fontSize: 20, minWidth: 28, textAlign: 'center' }}>{getCartQty(food.id)}</span>
                  {/* Increment */}
                  <button
                    onClick={() => updateCartItem(food.id, getCartQty(food.id) + 1)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: NEUTRAL_WHITE,
                      fontSize: 24,
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderRadius: 8
                    }}
                    aria-label="Increase quantity"
                  >+</button>
                </div>
              )}
            </div>
          </div>

          <BottomNavigation />
        </>
      )}
    </div>
  );
}

export default FoodDetail; 